import {
  useContext,
  createContext,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { api, errorTreatment } from "../../services/api";
import { LoadingContext } from "../Loading/LoadingContext";
import { AxiosError } from "axios";

interface FilterContextData {
  couriers: { value: string; label: string }[];
  regions: { value: string; label: string }[];
  shippers: { value: string; label: string }[];
  routeTypes: { value: string; label: string }[];
  completeRequisition: boolean;
}
interface FilterContextDataResponse {
  data: {
    cnpj: string;
    name: string;
  }[];
}

interface FilterProviderProps {
  children: ReactNode;
}

export const FilterContext = createContext({} as FilterContextData);

export function FilterProvider({ children }: FilterProviderProps) {
  const [couriers, setCouriers] = useState([{ value: "", label: "" }]);
  const [regions, setRegions] = useState([{ value: "", label: "" }]);
  const [shippers, setShippers] = useState([{ value: "", label: "" }]);
  const [routeTypes, setRouteTypes] = useState([{ value: "", label: "" }]);
  const [completeRequisition, setCompleteRequisition] = useState(false);

  const { changeVisibility } = useContext(LoadingContext);

  useEffect(() => {
    changeVisibility(true);
    Promise.all([
      api.get("/couriers"),
      api.get("/regions"),
      api.get("/shippers"),
      api.get("/routeTypes"),
    ])
      .then(
        ([couriersData, regionsData, shippersData, routeTypesData]: [
          FilterContextDataResponse,
          FilterContextDataResponse,
          FilterContextDataResponse,
          {data: { id: number; name: string ;}[]}
        ]) => {
          couriersData.data.forEach(
            (courier: { cnpj: string; name: string }) => {
              couriers.push({ value: courier.cnpj, label: courier.name });
            }
          );
          regionsData.data.forEach((region: { cnpj: string; name: string }) => {
            regions.push({ value: region.name, label: region.name });
          });
          shippersData.data.forEach(
            (shipper: { cnpj: string; name: string }) => {
              shippers.push({ value: shipper.cnpj, label: shipper.name });
            }
          );
          routeTypesData.data.forEach((routeType: { id: number; name: string }) => {
            routeTypes.push({ value: routeType.id.toString(), label: routeType.name });
          });
          setCouriers(couriers);
          setRegions(regions);
          setShippers(shippers);
          setRouteTypes(routeTypes);
        }
      )
      .catch((error: AxiosError) => {
        errorTreatment(error);
      })
      .finally(() => {
        changeVisibility(false);
        setCompleteRequisition(true);
      });
  }, []);

  return (
    <FilterContext.Provider
      value={{ couriers, regions, shippers, routeTypes, completeRequisition }}
    >
      {children}
    </FilterContext.Provider>
  );
}
